import { useEffect, useMemo } from "react";
import { prefetchPostContent, type BlogLocale } from "./posts.ts";

type IdleHandle = number;
interface IdleWindow {
  requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => IdleHandle;
  cancelIdleCallback?: (handle: IdleHandle) => void;
}

function scheduleIdle(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const w = window as IdleWindow & Window;
  if (typeof w.requestIdleCallback === "function") {
    const handle = w.requestIdleCallback(cb, { timeout: 2000 });
    return () => w.cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(cb, 300);
  return () => window.clearTimeout(handle);
}

/** Handlers to attach to a post link to warm its content cache on hover/focus. */
export function postPrefetchHandlers(slug: string, locale: BlogLocale = "en") {
  const warm = () => prefetchPostContent(slug, locale);
  return {
    onMouseEnter: warm,
    onFocus: warm,
    onTouchStart: warm,
  };
}

/** On mount, prefetch the first few visible post bodies on idle. */
export function useIdlePrefetchPosts(slugs: string[], count = 3, locale: BlogLocale = "en"): void {
  const targets = useMemo(() => slugs.slice(0, count), [slugs, count]);
  useEffect(() => {
    const cancel = scheduleIdle(() => {
      for (const slug of targets) prefetchPostContent(slug, locale);
    });
    return cancel;
  }, [targets, locale]);
}
